import { ChevronDown, Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useBranches } from '../../lib/options';
import { Spinner } from './Spinner';

/**
 * A branch is chosen by typing to filter and picking from a dropdown, not a native <select> —
 * scrolling a plain select list gets hard to use once a tenant has more than a handful of
 * branches, especially on mobile. Filters client-side over the already-loaded branch list
 * (unlike StaffPicker, which hits a search endpoint) since the full roster is already fetched
 * everywhere via useBranches() and tenants don't have enough branches to need pagination.
 */
export function BranchPicker({ value, onChange, placeholder = 'Search branches…', clearLabel, className, required }: {
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  /** Shown as the top row of the results dropdown to clear the selection — omit to require a pick. */
  clearLabel?: string;
  className?: string;
  required?: boolean;
}) {
  const branches = useBranches();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = branches.data?.find((b) => b.id === value);
  const results = useMemo(() => {
    const list = branches.data ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((b) => b.name.toLowerCase().includes(q) || b.code.toLowerCase().includes(q));
  }, [branches.data, query]);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  function openSearch() {
    setQuery('');
    setOpen(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  function pick(id: string) {
    onChange(id);
    setQuery('');
    setOpen(false);
  }

  return (
    <div className={`relative mt-1 ${className ?? ''}`} ref={containerRef}>
      {value && !open ? (
        <button type="button" className="field flex w-full items-center justify-between text-left" onClick={openSearch}>
          <span className="truncate">{selected ? selected.name : <Spinner size="sm" />}</span>
          <div className="flex shrink-0 items-center gap-1">
            {clearLabel && (
              <span role="button" aria-label="Clear" className="rounded-full p-1 text-muted hover:bg-canvas hover:text-ink" onClick={(e) => { e.stopPropagation(); onChange(''); }}>
                <X size={14} />
              </span>
            )}
            <ChevronDown size={14} className="text-muted" />
          </div>
        </button>
      ) : (
        <div className="relative">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <input
            ref={inputRef}
            className="field pl-9"
            placeholder={placeholder}
            required={required && !value}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setOpen(true)}
          />
        </div>
      )}

      {open && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-xl border border-border bg-white shadow-lg">
          {clearLabel && (
            <button type="button" className="block w-full px-3 py-2 text-left text-sm font-medium text-muted hover:bg-canvas" onClick={() => pick('')}>{clearLabel}</button>
          )}
          {results.length === 0 && <p className="px-3 py-2 text-sm text-muted">No branches found.</p>}
          {results.map((b) => (
            <button type="button" key={b.id} className="block w-full px-3 py-2 text-left text-sm hover:bg-canvas" onClick={() => pick(b.id)}>
              <span className="font-medium">{b.name}</span> <span className="text-muted">({b.code})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
